import { ArrowRight, ChevronRight, CheckCircle, Zap, Award, Star } from "lucide-react";
import "./Hero.css";
import { useNavigate } from "react-router-dom";

import largeFormatImg from '../../assets/images/largeFormat.jpeg';
import bags1     from '../../assets/images/bags1.jpg';
import bags2     from '../../assets/images/bags2.jpeg';
import boxes     from '../../assets/images/boxes.jpeg';
import threeD1   from '../../assets/images/threeD2.jpeg';
import threeD2   from '../../assets/images/threeD.jpg';
import threeD3   from '../../assets/images/threeD1.jpg';
import brand1    from '../../assets/images/brand1.jpeg';
import brand2    from '../../assets/images/brand2.jpeg';
import brand3    from '../../assets/images/brand3.jpeg';
import brand4    from '../../assets/images/brand4.jpg';
import brand5    from '../../assets/images/brand5.jpg';
import brochure  from '../../assets/images/brochure.jpg';
import flyers    from '../../assets/images/flyers.jpg';
import gift1     from '../../assets/images/gift1.jpg';
import bdCard    from '../../assets/images/bdCard.jpg';
import tshirt    from '../../assets/images/tshirt.jpg';
import wristband from '../../assets/images/wristband.jpg';
import caps      from '../../assets/images/Caps.jpg';
import book1     from '../../assets/images/book1.jpeg';
import book2     from '../../assets/images/book2.jpeg';
import book3     from '../../assets/images/book3.jpeg';
import book4     from '../../assets/images/book4.jpg';

// ─── Data ──────────────────────────────────────────────────────
const IMAGES = [
  { src: book1,        alt: "Book printing" },
  { src: book2,        alt: "Book design" },
  { src: book3,        alt: "Published book" },
  { src: book4,        alt: "Book cover" },
  { src: largeFormatImg, alt: "Large format print" },
  { src: bags1,        alt: "Branded bags" },
  { src: bags2,        alt: "Paper bags" },
  { src: boxes,        alt: "Packaging boxes" },
  { src: threeD1,      alt: "3D signage" },
  { src: threeD2,      alt: "3D letters" },
  { src: threeD3,      alt: "Illuminated sign" },
  { src: brand1,       alt: "Brand identity" },
  { src: brand2,       alt: "Logo design" },
  { src: brand3,       alt: "Brand guidelines" },
  { src: brand4,       alt: "Visual identity" },
  { src: brand5,       alt: "Brand collateral" },
  { src: brochure,     alt: "Brochure print" },
  { src: flyers,       alt: "Flyer design" },
  { src: gift1,        alt: "Corporate gift" },
  { src: bdCard,       alt: "Business cards" },
  { src: tshirt,       alt: "Branded t-shirt" },
  { src: wristband,    alt: "Wristbands" },
  { src: caps,         alt: "Branded caps" },
];

// Row config: direction, animation speed, image-list offset so no column lines up vertically
const ROWS = [
  { dir: "l", dur: "52s", off: 0  },
  { dir: "r", dur: "64s", off: 5  },
  { dir: "l", dur: "58s", off: 10 },
  { dir: "r", dur: "70s", off: 15 },
];

// Shift + duplicate so the marquee loop is seamless at -50%
function rowImages(offset) {
  const shifted = [...IMAGES.slice(offset), ...IMAGES.slice(0, offset)];
  return [...shifted, ...shifted]; // duplicate for seamless loop
}

const TRUST = [
  { Icon: CheckCircle, stat: "100+ Projects", sub: "Delivered" },
  { Icon: Zap,         stat: "Fast Turnaround", sub: "Guaranteed" },
  { Icon: Award,       stat: "Premium", sub: "Quality" },
];

// import { useNavigate } from "react-router-dom";
// const navigate = useNavigate();
  //  <button className="ft-btn-r ft-btn--lg" onClick={() => navigate("/contact")}>
  //     Get Free Quote <ArrowRight size={16} />
  //   </button>

// ─── Component ─────────────────────────────────────────────────
export default function Hero() {
  const navigate = useNavigate();
  return (
    <section className="hx" aria-label="Hero">

      {/* ── Scrolling image background ── */}
      <div className="hx-bg" aria-hidden="true">
        {ROWS.map((row, ri) => (
          <div className="hx-row" key={ri}>
            <div
              className={`hx-track hx-track--${row.dir}`}
              style={{ "--dur": row.dur }}
            >
              {rowImages(row.off).map((img, i) => (
                <div className="hx-item" key={i}>
                  <img src={img.src} alt={img.alt} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Left + right edge fades — sit inside hx-bg so they move with the mask */}
        <div className="hx-edge hx-edge--left"  aria-hidden="true" />
        <div className="hx-edge hx-edge--right" aria-hidden="true" />
      </div>

      {/* ── Overlays ── */}
      <div className="hx-scrim" aria-hidden="true" />
      <div className="hx-glow"  aria-hidden="true" />

      {/* ── Main content ── */}
      <div className="hx-inner">

        <div className="hx-eyebrow">
          <span className="hx-dot" />
          Nairobi's Premium Design Studio
        </div>

        <h1 className="hx-h1">
          Transforming <span className="hx-ideas">Ideas</span>
          <br />Into Powerful Brands
        </h1>

        <p className="hx-sub">
          We help businesses stand out through professional branding, printing,
          3D signage, packaging, promotional merchandise, and modern web design.
        </p>

        <div className="hx-btns">
           <button className="ft-btn-r ft-btn--lg" onClick={() => navigate("/contact")}>
             Get Free Quote <ArrowRight size={16} />
          </button>

          <button className="ft-btn-g ft-btn--lg" onClick={() => navigate("/services")}>
            Explore Our Work <ChevronRight size={16} />
          </button>
        </div>

        <div className="hx-trust">
          {TRUST.map(({ Icon, stat, sub }) => (
            <div className="hx-trust-item" key={stat}>
              <Icon size={17} color="#FF2B2B" aria-hidden="true" />
              <div>
                <span className="hx-trust-stat">{stat}</span>
                <span className="hx-trust-sub">{sub}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="hx-rating" aria-label="Rated 5 stars by 100+ brands">
          <div className="hx-stars">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={12} fill="#FF2B2B" color="#FF2B2B" aria-hidden="true" />
            ))}
          </div>
          <span className="hx-rating-text">100+ brands built across Kenya</span>
        </div>

      </div>
    </section>
  );
}